import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { NonInteractiveRule } from './validation';

interface NonInteractiveRulesProps {
    rules: NonInteractiveRule[];
    onUpdate: (rules: NonInteractiveRule[]) => void;
    storeId: string;
}

/**
 * NonInteractiveRules - Editor for conditional rules that auto-add order bumps
 * Rules determine when a non-interactive order bump is automatically added to cart
 */
export const NonInteractiveRules: React.FC<NonInteractiveRulesProps> = ({
    rules,
    onUpdate,
    storeId,
}) => {
    const { t } = useTranslation('upsells');
    const addRule = () => {
        const newRule: NonInteractiveRule = {
            type: 'CartContainsProduct',
            productId: '',
        };
        onUpdate([...rules, newRule]);
    };

    const updateRule = (index: number, updatedRule: NonInteractiveRule) => {
        const updated = [...rules];
        updated[index] = updatedRule;
        onUpdate(updated);
    };

    const deleteRule = (index: number) => {
        onUpdate(rules.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground">
                    {t('form.rules.description')}
                </p>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                            <p className="text-sm">
                                {t('form.rules.tooltip')}
                            </p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>

            {rules.map((rule, index) => (
                <NonInteractiveRuleCard
                    key={index}
                    rule={rule}
                    index={index}
                    onUpdate={(updatedRule) => updateRule(index, updatedRule)}
                    onDelete={() => deleteRule(index)}
                    storeId={storeId}
                />
            ))}

            <Button type="button" variant="outline" onClick={addRule} className="w-full">
                <Plus className="mr-2 h-4 w-4" />
                {t('form.rules.addRule')}
            </Button>

            {rules.length === 0 && (
                <p className="text-muted-foreground text-sm text-center py-4">
                    {t('form.rules.noRules')}
                </p>
            )}
        </div>
    );
};

interface NonInteractiveRuleCardProps {
    rule: NonInteractiveRule;
    index: number;
    onUpdate: (rule: NonInteractiveRule) => void;
    onDelete: () => void;
    storeId: string;
}

/**
 * NonInteractiveRuleCard - Single conditional rule editor
 */
const NonInteractiveRuleCard: React.FC<NonInteractiveRuleCardProps> = ({
    rule,
    index,
    onUpdate,
    onDelete,
    storeId,
}) => {
    const { t } = useTranslation('upsells');
    return (
        <Card>
            <CardContent className="pt-6">
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">{t('form.rules.ruleNumber', { index: index + 1 })}</Label>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={onDelete}
                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Rule Type Selector */}
                    <div className="space-y-2">
                        <Label>{t('form.rules.ruleTypeLabel')}</Label>
                        <Select
                            value={rule.type}
                            onValueChange={(value: NonInteractiveRule['type']) => {
                                // Reset rule-specific fields when type changes
                                onUpdate({
                                    type: value,
                                    ...(value === 'CartContainsProduct' ? { productId: '' } : {}),
                                    ...(value === 'CartTotalAbove' || value === 'CartTotalBelow'
                                        ? { minimumAmount: {} }
                                        : {}),
                                    ...(value === 'CartItemCountAbove' || value === 'CartItemCountBelow'
                                        ? { itemCount: 0 }
                                        : {}),
                                });
                            }}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="CartContainsProduct">{t('form.rules.types.cartContainsProduct')}</SelectItem>
                                <SelectItem value="CartTotalAbove">{t('form.rules.types.cartTotalAbove')}</SelectItem>
                                <SelectItem value="CartTotalBelow">{t('form.rules.types.cartTotalBelow')}</SelectItem>
                                <SelectItem value="CartItemCountAbove">{t('form.rules.types.cartItemCountAbove')}</SelectItem>
                                <SelectItem value="CartItemCountBelow">{t('form.rules.types.cartItemCountBelow')}</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-muted-foreground text-xs">
                            {t(`form.rules.descriptions.${rule.type}` as any)}
                        </p>
                    </div>

                    {/* Conditional Fields Based on Rule Type */}
                    {rule.type === 'CartContainsProduct' && (
                        <div className="space-y-2">
                            <Label>{t('form.rules.productIdLabel')}</Label>
                            <Input
                                placeholder={t('form.rules.productIdPlaceholder')}
                                value={rule.productId || ''}
                                onChange={(e) =>
                                    onUpdate({
                                        ...rule,
                                        productId: e.target.value,
                                    })
                                }
                            />
                            <p className="text-muted-foreground text-xs">
                                {t('form.rules.productIdHelper')}
                            </p>
                        </div>
                    )}

                    {(rule.type === 'CartTotalAbove' || rule.type === 'CartTotalBelow') && (
                        <div className="space-y-2">
                            <Label>{t('form.rules.amountLabel')}</Label>
                            <Input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder={t('form.rules.amountPlaceholder')}
                                value={rule.minimumAmount?.USD?.amount || ''}
                                onChange={(e) => {
                                    const amount = parseFloat(e.target.value) || 0;
                                    onUpdate({
                                        ...rule,
                                        minimumAmount: {
                                            USD: {
                                                amount,
                                                rate: 1,
                                                lock: false,
                                                date: new Date().toISOString(),
                                            },
                                        },
                                    });
                                }}
                            />
                            <p className="text-muted-foreground text-xs">
                                {rule.type === 'CartTotalAbove'
                                    ? t('form.rules.amountAboveDescription')
                                    : t('form.rules.amountBelowDescription')}
                            </p>
                            <p className="text-muted-foreground text-xs">
                                {t('form.rules.amountNote')}
                            </p>
                        </div>
                    )}

                    {(rule.type === 'CartItemCountAbove' || rule.type === 'CartItemCountBelow') && (
                        <div className="space-y-2">
                            <Label>{t('form.rules.itemCountLabel')}</Label>
                            <Input
                                type="number"
                                min="0"
                                step="1"
                                placeholder={t('form.rules.itemCountPlaceholder')}
                                value={rule.itemCount || ''}
                                onChange={(e) =>
                                    onUpdate({
                                        ...rule,
                                        itemCount: parseInt(e.target.value) || 0,
                                    })
                                }
                            />
                            <p className="text-muted-foreground text-xs">
                                {rule.type === 'CartItemCountAbove'
                                    ? t('form.rules.itemCountAboveDescription')
                                    : t('form.rules.itemCountBelowDescription')}
                            </p>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

